import { apiClient } from './client'
import type { FeatureConfig, UserFeatures as UserFeaturesType } from '@/types/user'

export interface SetFeatureRequest {
  value: boolean | FeatureConfig
}

export interface BulkUpdateFeaturesRequest {
  features: UserFeaturesType
}

export interface FeatureResponse {
  success: boolean
  data: {
    featureKey: string
    value: boolean | FeatureConfig
  }
}

export interface FeaturesResponse {
  success: boolean
  data: {
    features: UserFeaturesType
  }
}

export interface FeatureEnabledResponse {
  success: boolean
  data: {
    featureKey: string
    enabled: boolean
  }
}

export interface FeatureMutationResponse {
  success: boolean
  data: {
    message: string
    features: UserFeaturesType
  }
}

export const userFeaturesApi = {
  /**
   * Get all features for a user
   */
  getFeatures: async (userId: string): Promise<FeaturesResponse> => {
    const response = await apiClient.get<FeaturesResponse>(`/users/${userId}/features`)
    return response.data
  },

  /**
   * Get a specific feature for a user
   */
  getFeature: async (userId: string, featureKey: string): Promise<FeatureResponse> => {
    const response = await apiClient.get<FeatureResponse>(`/users/${userId}/features/${featureKey}`)
    return response.data
  },

  /**
   * Set or update a specific feature
   */
  setFeature: async (
    userId: string,
    featureKey: string,
    data: SetFeatureRequest
  ): Promise<FeatureMutationResponse> => {
    const response = await apiClient.put<FeatureMutationResponse>(
      `/users/${userId}/features/${featureKey}`,
      data
    )
    return response.data
  },

  /**
   * Remove a feature from a user
   */
  removeFeature: async (userId: string, featureKey: string): Promise<FeatureMutationResponse> => {
    const response = await apiClient.delete<FeatureMutationResponse>(
      `/users/${userId}/features/${featureKey}`
    )
    return response.data
  },

  /**
   * Bulk update all features for a user
   */
  bulkUpdateFeatures: async (
    userId: string,
    data: BulkUpdateFeaturesRequest
  ): Promise<FeatureMutationResponse> => {
    const response = await apiClient.post<FeatureMutationResponse>(
      `/users/${userId}/features/bulk`,
      data
    )
    return response.data
  },

  /**
   * Check if a specific feature is enabled
   */
  isFeatureEnabled: async (userId: string, featureKey: string): Promise<FeatureEnabledResponse> => {
    const response = await apiClient.get<FeatureEnabledResponse>(
      `/users/${userId}/features/${featureKey}/enabled`
    )
    return response.data
  },
}
